import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  GET_INQUIRY, 
  UPDATE_INQUIRY_STATUS, 
  UPDATE_INQUIRY_STATUS_WITH_CONTACT_INFO,
  CONVERT_INQUIRY_TO_CASE,
  LOG_INQUIRY_ACTIVITY,
  UPDATE_INQUIRY_TRACKING,
  GET_INQUIRY_EMAIL_URL,
  REJECT_INQUIRY,
  LIST_VIDEO_CONFERENCES_BY_INQUIRY
} from '../graphql/queries';
import { LIST_TASKS_BY_CASE, CREATE_TASK } from '../graphql/tasks';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { Navigation } from '../components/Navigation';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DocumentUpload } from '../components/DocumentUpload';
import { DocumentThumbnail } from '../components/DocumentThumbnail';
import { FileViewerModal, FileItem } from '../components/FileViewerModal';
import { DynamicInquiryDetail } from '../components/DynamicInquiryDetail';
import { EmailViewer } from '../components/EmailViewer';
import { ProspectConfirmationDialog, ContactInfo } from '../components/inquiry/ProspectConfirmationDialog';
import { CaseManagerAssignment } from '../components/inquiry/CaseManagerAssignment';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { VideoConferencesList } from '../components/video/VideoConferencesList';
import { ScheduleConferenceModal } from '../components/video/ScheduleConferenceModal';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const GET_INQUIRY_ATTACHMENT_DOWNLOAD_URL = gql`
  query GetInquiryAttachmentDownloadUrl($inquiryId: ID!, $attachmentId: ID!) {
    getInquiryAttachmentDownloadUrl(inquiryId: $inquiryId, attachmentId: $attachmentId)
  }
`;

const LIST_DOCUMENTS_BY_CASE = gql`
  query ListDocumentsByCase($caseId: ID!) {
    listDocumentsByCase(caseId: $caseId) {
      id
      fileName
      fileType
      fileSize
      description
      createdAt
      uploadedBy
      uploader {
        id
        firstName
        lastName
      }
    }
  }
`;

const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query GetDocumentDownloadUrl($id: ID!) {
    getDocumentDownloadUrl(id: $id)
  }
`;

const LIST_APPOINTMENTS_BY_CASE = gql`
  query ListAppointmentsByCase($caseId: ID!) {
    listAppointmentsByCase(caseId: $caseId) {
      id
      title
      startTime
      endTime
      location
      description
    }
  }
`;

// Type definitions for Apollo queries/mutations
interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface Inquiry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  status: string;
  incidentType: string;
  leadScore: number;
  caseId?: string;
  userId?: string;
  source?: string;
  emailS3Key?: string;
  dynamicInquiryData?: any;
  attachments?: any[];
  activityLog?: any[];
  lastContactedAt?: string;
  lastContactMethod?: string;
  followUpDate?: string;
  assignedToName?: string;
  assignedCaseManager?: any;
  [key: string]: any;
}

interface GetInquiryData {
  getInquiry: Inquiry;
}

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  description?: string;
  createdAt: string;
  uploadedBy?: string;
  uploader?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface ListDocumentsByCaseData {
  listDocumentsByCase: Document[];
}

interface Appointment {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  location?: string;
  description?: string;
}

interface ListAppointmentsByCaseData {
  listAppointmentsByCase: Appointment[];
}

interface VideoConference {
  id: string;
  title: string;
  status: string;
  scheduledStartTime: string;
  scheduledEndTime?: string;
  hostId: string;
  host?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface ListVideoConferencesByInquiryData {
  listVideoConferencesByInquiry: VideoConference[];
}

interface GetInquiryAttachmentDownloadUrlData {
  getInquiryAttachmentDownloadUrl: string;
}

interface GetInquiryEmailUrlData {
  getInquiryEmailUrl: string;
}

interface GetDocumentDownloadUrlData {
  getDocumentDownloadUrl: string;
}

interface UpdateInquiryStatusData {
  updateInquiryStatus: {
    __typename: string;
    id: string;
    status: string;
    updatedAt: string;
  };
}

export function InquiryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'activity' | 'appointments' | 'tasks'>('overview');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEmailViewer, setShowEmailViewer] = useState(false);
  const [showProspectDialog, setShowProspectDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [showVideoConferenceModal, setShowVideoConferenceModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [prospectLoading, setProspectLoading] = useState(false);
  const [rejectionLoading, setRejectionLoading] = useState(false);
  const [emailUrl, setEmailUrl] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [currentStatus, setCurrentStatus] = useState<string>('');
  
  // Task form state
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskType, setTaskType] = useState('DOCUMENT_UPLOAD');
  const [taskPriority, setTaskPriority] = useState('MEDIUM');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskStaffNotes, setTaskStaffNotes] = useState('');  
  // File viewer state
  const [fileViewerVisible, setFileViewerVisible] = useState(false);
  const [fileViewerFiles, setFileViewerFiles] = useState<FileItem[]>([]);
  const [fileViewerIndex, setFileViewerIndex] = useState(0);

  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data, loading, error, refetch } = useQuery<GetInquiryData>(GET_INQUIRY, {
    variables: { id },
    skip: !id,
  });

  const [updateStatus] = useMutation<UpdateInquiryStatusData>(UPDATE_INQUIRY_STATUS, {
    // Optimistic response for instant UI update
    optimisticResponse: (vars) => ({
      updateInquiryStatus: {
        __typename: 'Inquiry',
        id: vars.id,
        status: vars.status,
        updatedAt: new Date().toISOString(),
      },
    }),
    // Update cache immediately
    update: (cache, { data }) => {
      if (data?.updateInquiryStatus) {
        cache.modify({
          id: cache.identify({ __typename: 'Inquiry', id }),
          fields: {
            status() {
              return data.updateInquiryStatus.status;
            },
            updatedAt() {
              return data.updateInquiryStatus.updatedAt;
            },
          },
        });
      }
    },
  });
  
  const [logActivity] = useMutation(LOG_INQUIRY_ACTIVITY);
  const [updateTracking] = useMutation(UPDATE_INQUIRY_TRACKING, {
    fetchPolicy: 'no-cache',
  });
  const [updateStatusWithContactInfo] = useMutation(UPDATE_INQUIRY_STATUS_WITH_CONTACT_INFO);
  const [rejectInquiry] = useMutation(REJECT_INQUIRY);
  
  const apolloClient = useApolloClient();
  const [getEmailUrl] = useLazyQuery<GetInquiryEmailUrlData>(GET_INQUIRY_EMAIL_URL);

  const inquiry = data?.getInquiry;
  const hasUserAccount = !!inquiry?.userId;
  const hasCaseId = !!inquiry?.caseId;

  // Memoized wrapper for attachment download URL - uses apolloClient.query to avoid abort issues
  const getAttachmentUrlForThumbnail = useCallback(async ({ variables }: { variables: { id: string } }) => {
    const { data } = await apolloClient.query<GetInquiryAttachmentDownloadUrlData>({
      query: GET_INQUIRY_ATTACHMENT_DOWNLOAD_URL,
      variables: { inquiryId: id, attachmentId: variables.id },
      fetchPolicy: 'network-only',
    });
    return { data: { getDocumentDownloadUrl: data?.getInquiryAttachmentDownloadUrl } };
  }, [apolloClient, id]);

  // Memoized wrapper for case document download URL - uses apolloClient.query to avoid abort issues
  const getDocumentUrlForThumbnail = useCallback(async ({ variables }: { variables: { id: string } }) => {
    const { data } = await apolloClient.query<GetDocumentDownloadUrlData>({
      query: GET_DOCUMENT_DOWNLOAD_URL,
      variables: { id: variables.id },
      fetchPolicy: 'network-only',
    });
    return { data: { getDocumentDownloadUrl: data?.getDocumentDownloadUrl } };
  }, [apolloClient]);

  // Fetch documents from placeholder case (for inquiries with linked user accounts)
  const { data: docsData, refetch: refetchDocs } = useQuery<ListDocumentsByCaseData>(LIST_DOCUMENTS_BY_CASE, {
    variables: { caseId: inquiry?.caseId },
    skip: !inquiry?.caseId,
  });

  // Fetch appointments from placeholder case (for inquiries with linked user accounts)
  const { data: appointmentsData, refetch: _refetchAppointments } = useQuery<ListAppointmentsByCaseData>(LIST_APPOINTMENTS_BY_CASE, {
    variables: { caseId: inquiry?.caseId },
    skip: !inquiry?.caseId,
  });

  // Fetch video conferences for this inquiry
  const { data: videoConferencesData, loading: videoConferencesLoading, refetch: refetchVideoConferences } = useQuery<ListVideoConferencesByInquiryData>(LIST_VIDEO_CONFERENCES_BY_INQUIRY, {
    variables: { inquiryId: id },
    skip: !id,
  });

  // Fetch tasks for inquiries with linked user accounts (tasks are stored by caseId)
  const { data: tasksData, refetch: refetchTasks } = useQuery<{ listTasksByCase: { items: any[] } }>(LIST_TASKS_BY_CASE, {
    variables: { caseId: inquiry?.caseId },
    skip: !inquiry?.caseId,
    fetchPolicy: 'network-only',
  });

  const [createTask] = useMutation(CREATE_TASK);

  const caseDocuments = docsData?.listDocumentsByCase || [];
  const appointments = appointmentsData?.listAppointmentsByCase || [];
  const videoConferences = videoConferencesData?.listVideoConferencesByInquiry || [];
  const tasks = tasksData?.listTasksByCase?.items || [];

  // Sync local status state when inquiry data changes
  useEffect(() => {
    if (inquiry?.status) {
      setCurrentStatus(inquiry.status);
    }
  }, [inquiry?.status]);

  const handleViewEmail = async () => {
    try {
      const { data } = await getEmailUrl({
        variables: { inquiryId: id },
      });

      if (data?.getInquiryEmailUrl) {
        setEmailUrl(data.getInquiryEmailUrl);
        setShowEmailViewer(true);
      } else {
        alert('No email associated with this inquiry');
      }
    } catch (error: any) {
      console.error('Error getting email URL:', error);
      alert('Error viewing email: ' + error.message);
    }
  };

  const handleDownloadAttachment = async (attachmentId: string) => {
    try {
      const { data } = await apolloClient.query<GetInquiryAttachmentDownloadUrlData>({
        query: GET_INQUIRY_ATTACHMENT_DOWNLOAD_URL,
        variables: { inquiryId: id, attachmentId },
        fetchPolicy: 'network-only',
      });

      if (data?.getInquiryAttachmentDownloadUrl) {
        // Open the pre-signed URL in a new tab to download
        window.open(data.getInquiryAttachmentDownloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Error downloading attachment:', error);
      alert('Error downloading attachment: ' + error.message);
    }
  };

  // Unified preview handler for all documents (attachments + case documents)
  const handlePreviewAllDocuments = async (clickedIndex: number, isAttachment: boolean) => {
    try {
      const allAttachments = inquiry?.attachments || [];
      
      // Load attachment URLs using apolloClient.query
      const attachmentPromises = allAttachments.map(async (att: any) => {
        const { data } = await apolloClient.query<GetInquiryAttachmentDownloadUrlData>({
          query: GET_INQUIRY_ATTACHMENT_DOWNLOAD_URL,
          variables: { inquiryId: id, attachmentId: att.id },
          fetchPolicy: 'network-only',
        });
        return {
          url: data?.getInquiryAttachmentDownloadUrl || '',
          fileName: att.fileName,
          fileType: att.fileType,
        };
      });
      
      // Load case document URLs using apolloClient.query
      const docPromises = caseDocuments.map(async (d: any) => {
        const { data } = await apolloClient.query<GetDocumentDownloadUrlData>({
          query: GET_DOCUMENT_DOWNLOAD_URL,
          variables: { id: d.id },
          fetchPolicy: 'network-only',
        });
        return {
          url: data?.getDocumentDownloadUrl || '',
          fileName: d.fileName,
          fileType: d.fileType,
        };
      });
      
      const [attachmentFiles, docFiles] = await Promise.all([
        Promise.all(attachmentPromises),
        Promise.all(docPromises),
      ]);
      
      // Combine all files: attachments first, then case documents
      const allFiles = [...attachmentFiles, ...docFiles].filter(f => f.url);
      
      // Calculate the correct index in the combined list
      const actualIndex = isAttachment 
        ? clickedIndex 
        : attachmentFiles.length + clickedIndex;
      
      setFileViewerFiles(allFiles);
      setFileViewerIndex(actualIndex);
      setFileViewerVisible(true);
    } catch (error: any) {
      console.error('Error loading document preview:', error);
    }
  };

  // Legacy handlers that call the unified handler
  const handlePreviewAttachment = async (_attachment: any, index: number, _allAttachments: any[]) => {
    handlePreviewAllDocuments(index, true);
  };

  const handlePreviewDocument = async (_doc: any, index: number) => {
    handlePreviewAllDocuments(index, false);
  };

  const handleStatusChange = async (newStatus: string) => {
    // If changing to REVIEWING and no user account exists, show the create user dialog
    if (newStatus === 'REVIEWING' && !hasUserAccount) {
      setShowProspectDialog(true);
      return;
    }

    // If changing to REJECTED and inquiry has a linked user, show rejection dialog
    if (newStatus === 'REJECTED' && inquiry?.userId) {
      setShowRejectionDialog(true);
      return;
    }

    // Update local state immediately for responsive UI
    const previousStatus = currentStatus;
    setCurrentStatus(newStatus);

    try {
      const result = await updateStatus({
        variables: { id, status: newStatus },
      });
      
      // Update local cache immediately
      if (result.data) {
        await refetch();
      }
      
      // Log activity (don't wait for it)
      logActivity({
        variables: {
          input: {
            inquiryId: id,
            activityType: 'STATUS_CHANGED',
            details: `Status changed to ${newStatus}`,
          },
        },
      }).catch(err => console.error('Error logging activity:', err));
      
    } catch (error: any) {
      // Revert local state on error
      setCurrentStatus(previousStatus);
      console.error('Error updating status:', error);
      alert('Error updating status: ' + error.message);
    }
  };

  // Handle creating user account for inquiry (for email inquiries)
  const handleCreateUserConfirm = async (editedContactInfo: ContactInfo) => {
    setProspectLoading(true);
    try {
      const result = await updateStatusWithContactInfo({
        variables: {
          id,
          status: 'REVIEWING',
          editedContactInfo: {
            email: editedContactInfo.email,
            firstName: editedContactInfo.firstName,
            lastName: editedContactInfo.lastName,
            phoneNumber: editedContactInfo.phoneNumber,
          },
        },
      });

      // Update local state immediately
      setCurrentStatus('REVIEWING');

      // Update local cache
      if (result.data) {
        await refetch();
      }

      // Log activity
      logActivity({
        variables: {
          input: {
            inquiryId: id,
            activityType: 'STATUS_CHANGED',
            details: 'Client account created for inquiry',
          },
        },
      }).catch(err => console.error('Error logging activity:', err));

      setShowProspectDialog(false);
      alert('Client account created successfully! They can now log in and access their inquiry.');
    } catch (error: any) {
      console.error('Error creating user account:', error);
      alert('Error creating client account: ' + error.message);
    } finally {
      setProspectLoading(false);
    }
  };

  // Handle user account dialog cancellation
  const handleProspectCancel = () => {
    setShowProspectDialog(false);
  };

  // Handle rejection with optional account disable
  const handleRejection = async (disableAccount: boolean) => {
    setRejectionLoading(true);
    try {
      const result = await rejectInquiry({
        variables: {
          inquiryId: id,
          disableAccount,
        },
      });

      // Update local state immediately
      setCurrentStatus('REJECTED');

      // Update local cache
      if (result.data) {
        await refetch();
      }

      setShowRejectionDialog(false);
      alert(disableAccount 
        ? 'Inquiry rejected and client account disabled.' 
        : 'Inquiry rejected. Client account remains active.');
    } catch (error: any) {
      console.error('Error rejecting inquiry:', error);
      alert('Error rejecting inquiry: ' + error.message);
    } finally {
      setRejectionLoading(false);
    }
  };

  // Handle rejection dialog cancellation
  const handleRejectionCancel = () => {
    setShowRejectionDialog(false);
  };

  const handleLogCall = async () => {
    try {
      console.log('Logging call for inquiry:', id);
      
      // Update tracking with proper input object
      const trackingResult = await updateTracking({
        variables: {
          id: id,
          input: {
            lastContactedAt: new Date().toISOString(),
            lastContactMethod: 'Phone Call',
          },
        },
      });
      
      console.log('Tracking updated:', trackingResult);
      
      // Log activity
      await logActivity({
        variables: {
          input: {
            inquiryId: id,
            activityType: 'CONTACTED_CLIENT',
            details: 'Phone call made to client',
          },
        },
      });
      
      // Refresh data
      await refetch();
      alert('Call logged successfully!');
    } catch (error: any) {
      console.error('Error logging call:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      alert('Error logging call: ' + (error.message || JSON.stringify(error)));
    }
  };

  const handleFieldCorrection = async (fieldName: string, oldValue: any, newValue: any) => {
    try {
      console.log('Field correction:', { fieldName, oldValue, newValue });
      
      // Log the correction as feedback for AI learning
      await logActivity({
        variables: {
          input: {
            inquiryId: id,
            activityType: 'NOTE_ADDED',
            details: `AI Field Correction: ${fieldName} changed from "${oldValue}" to "${newValue}"`,
            metadata: {
              type: 'ai_field_correction',
              fieldName,
              oldValue,
              newValue,
              correctedBy: userData?.getCurrentUser?.id
            }
          },
        },
      });
      
      // Refresh data to show the correction in activity log
      await refetch();
      alert('Field correction logged for AI learning');
    } catch (error: any) {
      console.error('Error logging field correction:', error);
      alert('Error logging correction: ' + error.message);
    }
  };

  const handleAddNote = async () => {
    if (!notes.trim()) {
      alert('Please enter a note');
      return;
    }
    
    try {
      console.log('Adding note for inquiry:', id);
      
      const result = await logActivity({
        variables: {
          input: {
            inquiryId: id,
            activityType: 'NOTE_ADDED',
            details: notes,
          },
        },
      });
      
      console.log('Note added:', result);
      
      setNotes('');
      setShowNotesModal(false);
      
      // Refresh data immediately
      await refetch();
      alert('Note added successfully!');
    } catch (error: any) {
      console.error('Error adding note:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      alert('Error adding note: ' + (error.message || JSON.stringify(error)));
    }
  };

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-red-600 bg-red-50';
    if (score >= 60) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      NEW: 'info',
      REVIEWING: 'warning',
      ACCEPTED: 'success',
      REJECTED: 'destructive',
    };
    return variants[status] || 'default';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Task helper functions
  const getTaskStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      PENDING: 'warning',
      IN_PROGRESS: 'info',
      COMPLETED: 'success',
      VERIFIED: 'success',
      REJECTED: 'default',
      OVERDUE: 'default',
    };
    return variants[status] || 'default';
  };

  const getTaskPriorityBadgeVariant = (priority: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      LOW: 'secondary',
      MEDIUM: 'info',
      HIGH: 'warning',
      URGENT: 'default',
    };
    return variants[priority] || 'default';
  };

  const getTaskTypeIcon = (type: string) => {
    const baseClasses = "w-5 h-5";
    const icons: Record<string, JSX.Element> = {
      DOCUMENT_UPLOAD: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      APPOINTMENT: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      FORM_COMPLETION: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      COMMUNICATION: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      OTHER: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      ),
    };
    return icons[type] || icons.OTHER;
  };

  const isTaskOverdue = (dueDate: string, status: string) => {
    if (status === 'COMPLETED' || status === 'VERIFIED') return false;
    return new Date(dueDate) < new Date();
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} days`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const handleCreateTask = async () => {
    if (!taskTitle.trim() || !taskDescription.trim() || !taskDueDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await createTask({
        variables: {
          input: {
            caseId: inquiry?.caseId,
            clientId: inquiry?.userId,
            title: taskTitle,
            description: taskDescription,
            taskType,
            priority: taskPriority,
            dueDate: taskDueDate,
            staffNotes: taskStaffNotes || undefined,
          },
        },
      });
      await refetchTasks();
      setShowCreateTaskModal(false);
      // Reset form
      setTaskTitle('');
      setTaskDescription('');
      setTaskType('DOCUMENT_UPLOAD');
      setTaskPriority('MEDIUM');
      setTaskDueDate('');
      setTaskStaffNotes('');
      alert('Task assigned successfully!');
    } catch (error: any) {
      console.error('Error creating task:', error);
      alert('Error creating task: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={userData?.getCurrentUser} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
        </div>
      </div>
    );
  }

  if (error || !inquiry) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={userData?.getCurrentUser} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600">
              {error ? 'Error loading inquiry' : 'Inquiry not found'}
            </h2>
            <p className="text-gray-600 mt-2">
              {error?.message || 'The inquiry you are looking for does not exist or has been removed.'}
            </p>
            <div className="flex gap-3 justify-center mt-4">
              <Button variant="outline" onClick={() => navigate('/inquiries')}>
                Back to Inquiries
              </Button>
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={userData?.getCurrentUser} />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                ← Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-aalaw-charcoal">
                  {inquiry.firstName} {inquiry.lastName}
                </h1>
                <p className="text-sm text-gray-600">{inquiry.incidentType}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={getStatusBadgeVariant(inquiry.status)}>
                {inquiry.status}
              </Badge>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getLeadScoreColor(inquiry.leadScore)}`}>
                Score: {inquiry.leadScore}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <Card>
              <CardContent className="p-0">
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'overview'
                        ? 'text-aalaw-red border-b-2 border-aalaw-red'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('documents')}
                    className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'documents'
                        ? 'text-aalaw-red border-b-2 border-aalaw-red'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Documents ({(inquiry.attachments?.length || 0) + caseDocuments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('activity')}
                    className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'activity'
                        ? 'text-aalaw-red border-b-2 border-aalaw-red'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Activity ({inquiry.activityLog?.length || 0})
                  </button>
                  {hasUserAccount && hasCaseId && (
                    <button
                      onClick={() => setActiveTab('appointments')}
                      className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                        activeTab === 'appointments'
                          ? 'text-aalaw-red border-b-2 border-aalaw-red'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Appointments ({appointments.length})
                    </button>
                  )}
                  {hasUserAccount && hasCaseId && (
                    <button
                      onClick={() => setActiveTab('tasks')}
                      className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                        activeTab === 'tasks'
                          ? 'text-aalaw-red border-b-2 border-aalaw-red'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Tasks ({tasks.length})
                    </button>
                  )}
                </div>

                <div className="p-6">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      {/* Dynamic AI-Generated Content (for AI-processed emails) */}
                      {inquiry.source === 'EMAIL' && inquiry.dynamicInquiryData?.uiSchema ? (
                        <DynamicInquiryDetail 
                          inquiry={inquiry as any} 
                          onFieldCorrection={handleFieldCorrection}
                        />
                      ) : (
                        <>
                          {/* Standard Static Content (for form submissions or legacy emails) */}
                      {/* Contact Information */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Source</label>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                inquiry.source === 'EMAIL' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {inquiry.source === 'EMAIL' ? '📧 Email' : '📝 Form'}
                              </span>
                              {inquiry.source === 'EMAIL' && inquiry.emailS3Key && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={handleViewEmail}
                                  className="text-xs"
                                >
                                  View Original Email
                                </Button>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Email</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.email}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Phone</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.phoneNumber}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Preferred Contact</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.preferredContactMethod || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Best Time</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.bestTimeToContact || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Zip Code</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.zipCode || 'N/A'}</p>
                          </div>
                          {inquiry.dateOfBirth && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Date of Birth</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.dateOfBirth}</p>
                            </div>
                          )}
                          {inquiry.spokenLanguage && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Spoken Language</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.spokenLanguage}</p>
                            </div>
                          )}
                          {inquiry.emergencyContactName && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Emergency Contact</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.emergencyContactName}</p>
                            </div>
                          )}
                          {inquiry.emergencyContactPhone && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Emergency Contact Phone</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.emergencyContactPhone}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Incident Details */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Incident Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Incident Type</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.incidentType}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Date</label>
                            <p className="text-sm text-gray-900 mt-1">{formatDate(inquiry.incidentDate)}</p>
                          </div>
                          <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-600">Location</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.incidentLocation || 'N/A'}</p>
                          </div>
                          {inquiry.crossStreets && (
                            <div className="col-span-2">
                              <label className="text-sm font-medium text-gray-600">Cross Streets</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.crossStreets}</p>
                            </div>
                          )}
                          <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-600">Description</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.description}</p>
                          </div>
                        </div>
                      </div>

                      {/* Injuries */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Injuries & Medical Treatment</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Injury Types</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {inquiry.injuryTypes?.join(', ') || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Severity</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.injurySeverity || 'N/A'}</p>
                          </div>
                          {inquiry.injuryDescription && (
                            <div className="col-span-2">
                              <label className="text-sm font-medium text-gray-600">Injury Description</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.injuryDescription}</p>
                            </div>
                          )}
                          <div>
                            <label className="text-sm font-medium text-gray-600">Medical Attention</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {inquiry.medicalAttentionSought ? 'Yes' : 'No'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Health Insurance</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {inquiry.hasHealthInsurance ? 'Yes' : 'No'}
                            </p>
                          </div>
                          {inquiry.treatmentDescription && (
                            <div className="col-span-2">
                              <label className="text-sm font-medium text-gray-600">Treatment Description</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.treatmentDescription}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Insurance & Legal */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Insurance & Legal Status</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Police Report Filed</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {inquiry.policeReportFiled ? 'Yes' : 'No'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">At Fault</label>
                            <p className="text-sm text-gray-900 mt-1">{inquiry.atFault || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Other Party Insured</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {inquiry.otherPartyInsured ? 'Yes' : 'No'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Has Attorney</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {inquiry.hasAttorney ? 'Yes' : 'No'}
                            </p>
                          </div>
                          {inquiry.lawsuitFiledDate && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Lawsuit Filed Date</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.lawsuitFiledDate}</p>
                            </div>
                          )}
                          {inquiry.tdRequestReason && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">TD Request Reason</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.tdRequestReason}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Broker/Referral Information (for email inquiries) */}
                      {inquiry.source === 'EMAIL' && (inquiry.referralTransactionNumber || inquiry.brokerCaseManager || inquiry.documentsUploaded || inquiry.referralLink) && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Information</h3>
                          <div className="grid grid-cols-2 gap-4">
                            {inquiry.referralTransactionNumber && (
                              <div>
                                <label className="text-sm font-medium text-gray-600">Referral Transaction Number</label>
                                <p className="text-sm text-gray-900 mt-1">{inquiry.referralTransactionNumber}</p>
                              </div>
                            )}
                            {inquiry.brokerCaseManager && (
                              <div>
                                <label className="text-sm font-medium text-gray-600">Case Manager</label>
                                <p className="text-sm text-gray-900 mt-1">{inquiry.brokerCaseManager}</p>
                              </div>
                            )}
                            {inquiry.brokerCaseManagerEmail && (
                              <div>
                                <label className="text-sm font-medium text-gray-600">Case Manager Email</label>
                                <p className="text-sm text-gray-900 mt-1">{inquiry.brokerCaseManagerEmail}</p>
                              </div>
                            )}
                            {inquiry.documentsUploaded && (
                              <div>
                                <label className="text-sm font-medium text-gray-600">Documents Uploaded</label>
                                <p className="text-sm text-gray-900 mt-1">{inquiry.documentsUploaded}</p>
                              </div>
                            )}
                            {inquiry.isPostConversion && (
                              <div>
                                <label className="text-sm font-medium text-gray-600">Post Conversion Referral</label>
                                <p className="text-sm text-gray-900 mt-1">Yes</p>
                              </div>
                            )}
                            {inquiry.referralLink && (
                              <div className="col-span-2">
                                <label className="text-sm font-medium text-gray-600">Referral Link</label>
                                <div className="mt-1">
                                  <Button
                                    onClick={() => window.open(inquiry.referralLink, '_blank')}
                                    variant="outline"
                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  >
                                    🔗 View Original Referral
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Email Processing Metadata (for admins) */}
                      {inquiry.source === 'EMAIL' && inquiry.extractionMetadata && userData?.getCurrentUser?.role === 'ADMIN' && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Processing Details</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-600">Extraction Source</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.extractionMetadata.source || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Confidence Score</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.extractionMetadata.confidence || 'N/A'}%</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Fields Extracted</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.extractionMetadata.totalFieldsExtracted || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Broker Name</label>
                              <p className="text-sm text-gray-900 mt-1">{inquiry.extractionMetadata.brokerName || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeTab === 'documents' && (
                    <div className="space-y-6">
                      {/* Upload Section for inquiries with linked user accounts */}
                      {hasUserAccount && hasCaseId && inquiry.caseId && (
                        <DocumentUpload
                          entityId={inquiry.caseId}
                          entityType="case"
                          onUploadComplete={() => {
                            refetchDocs();
                            logActivity({
                              variables: {
                                input: {
                                  inquiryId: id,
                                  activityType: 'ATTACHMENT_ADDED',
                                  details: 'Document uploaded to inquiry',
                                },
                              },
                            }).catch(err => console.error('Error logging activity:', err));
                          }}
                        />
                      )}

                      {/* All Documents - unified list */}
                      {((inquiry.attachments && inquiry.attachments.length > 0) || caseDocuments.length > 0) && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents</h3>
                          
                          {/* Original Inquiry Attachments */}
                          {inquiry.attachments?.map((attachment: any, index: number) => (
                            <div
                              key={attachment.id}
                              className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 mb-3 group"
                            >
                              <DocumentThumbnail
                                document={{ id: attachment.id, fileType: attachment.fileType, fileName: attachment.fileName }}
                                getDownloadUrl={getAttachmentUrlForThumbnail}
                                size={56}
                                onClick={() => handlePreviewAttachment(attachment, index, inquiry.attachments || [])}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handlePreviewAttachment(attachment, index, inquiry.attachments || [])}
                                    className="text-sm font-semibold text-gray-900 hover:text-aalaw-red transition-colors text-left truncate"
                                  >
                                    {attachment.fileName}
                                  </button>
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Original</span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">
                                  {(attachment.fileSize / 1024).toFixed(1)} KB • {formatDateTime(attachment.uploadedAt)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handlePreviewAttachment(attachment, index, inquiry.attachments || [])}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Preview"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDownloadAttachment(attachment.id)}
                                >
                                  Download
                                </Button>
                              </div>
                            </div>
                          ))}

                          {/* Case Documents (for inquiries with linked user accounts) */}
                          {caseDocuments.map((doc: any, index: number) => (
                            <div
                              key={doc.id}
                              className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 mb-3 group"
                            >
                              <DocumentThumbnail
                                document={doc}
                                getDownloadUrl={getDocumentUrlForThumbnail}
                                size={56}
                                onClick={() => handlePreviewDocument(doc, index)}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handlePreviewDocument(doc, index)}
                                    className="text-sm font-semibold text-gray-900 hover:text-aalaw-red transition-colors text-left truncate"
                                  >
                                    {doc.fileName}
                                  </button>
                                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Uploaded</span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">
                                  {(doc.fileSize / 1024 / 1024).toFixed(2)} MB • {formatDateTime(doc.createdAt)}
                                  {doc.uploader && ` • ${doc.uploader.firstName} ${doc.uploader.lastName}`}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handlePreviewDocument(doc, index)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Preview"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={async () => {
                                    const { data } = await apolloClient.query<GetDocumentDownloadUrlData>({
                                      query: GET_DOCUMENT_DOWNLOAD_URL,
                                      variables: { id: doc.id },
                                      fetchPolicy: 'network-only',
                                    });
                                    if (data?.getDocumentDownloadUrl) {
                                      window.open(data.getDocumentDownloadUrl, '_blank');
                                    }
                                  }}
                                >
                                  Download
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Empty state */}
                      {(!inquiry.attachments || inquiry.attachments.length === 0) && caseDocuments.length === 0 && (
                        <div className="text-center py-12">
                          <span className="text-5xl">📁</span>
                          <p className="text-gray-500 mt-4">No documents yet</p>
                          {!hasUserAccount && (
                            <p className="text-sm text-gray-400 mt-2">
                              Change status to REVIEWING to create a client account and enable document uploads
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Appointments Tab (for inquiries with linked user accounts) */}
                  {activeTab === 'appointments' && hasUserAccount && hasCaseId && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Scheduled Appointments</h3>
                        <Button
                          onClick={() => navigate(`/calendar?caseId=${inquiry.caseId}`)}
                          variant="outline"
                        >
                          + New Appointment
                        </Button>
                      </div>
                      
                      {appointments.length === 0 ? (
                        <div className="text-center py-12">
                          <span className="text-5xl">📅</span>
                          <p className="text-gray-500 mt-4">No appointments scheduled</p>
                          <p className="text-sm text-gray-400 mt-2">
                            Schedule a meeting with this client
                          </p>
                        </div>
                      ) : (
                        <>
                          {appointments
                            .sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                            .map((apt: any) => {
                              const isPast = new Date(apt.startTime) < new Date();
                              return (
                                <div
                                  key={apt.id}
                                  className={`flex items-center gap-4 p-4 border rounded-lg ${
                                    isPast ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-blue-200 bg-blue-50'
                                  }`}
                                >
                                  <div className={`text-center p-3 rounded-lg ${isPast ? 'bg-gray-200' : 'bg-blue-600 text-white'}`}>
                                    <div className="text-lg font-bold">
                                      {new Date(apt.startTime).getDate()}
                                    </div>
                                    <div className="text-xs uppercase">
                                      {new Date(apt.startTime).toLocaleDateString('en-US', { month: 'short' })}
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-semibold text-gray-900">{apt.title}</p>
                                    <p className="text-sm text-gray-600">
                                      {new Date(apt.startTime).toLocaleTimeString('en-US', { 
                                        hour: 'numeric', 
                                        minute: '2-digit' 
                                      })} - {new Date(apt.endTime).toLocaleTimeString('en-US', { 
                                        hour: 'numeric', 
                                        minute: '2-digit' 
                                      })}
                                    </p>
                                    {apt.location && (
                                      <p className="text-sm text-gray-500">📍 {apt.location}</p>
                                    )}
                                  </div>
                                  <Badge variant={isPast ? 'secondary' : 'info'}>
                                    {isPast ? 'Past' : apt.status || 'Scheduled'}
                                  </Badge>
                                </div>
                              );
                            })}
                        </>
                      )}
                    </div>
                  )}

                  {/* Activity Tab */}
                  {activeTab === 'activity' && (
                    <div className="space-y-4">
                      {!inquiry.activityLog || inquiry.activityLog.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No activity yet</p>
                      ) : (
                        inquiry.activityLog.map((activity: any) => (
                          <div key={activity.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
                            <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-aalaw-red"></div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {activity.activityType.replace(/_/g, ' ')}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">{activity.details}</p>
                                  {activity.performedByName && (
                                    <p className="text-xs text-gray-500 mt-1">by {activity.performedByName}</p>
                                  )}
                                </div>
                                <span className="text-xs text-gray-400">{formatDateTime(activity.timestamp)}</span>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Tasks Tab (for inquiries with linked user accounts) */}
                  {activeTab === 'tasks' && hasUserAccount && hasCaseId && (
                    <div className="space-y-4">
                      {/* Header with Create Button */}
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Client Tasks</h3>
                        <Button 
                          onClick={() => setShowCreateTaskModal(true)}
                        >
                          Assign New Task
                        </Button>
                      </div>

                      {/* Task List */}
                      {tasks.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          <p className="text-gray-600 mb-4">No tasks assigned yet</p>
                          <Button 
                            variant="outline"
                            onClick={() => setShowCreateTaskModal(true)}
                          >
                            Assign First Task
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {tasks.map((task: any) => {
                            const overdue = isTaskOverdue(task.dueDate, task.status);
                            return (
                              <div
                                key={task.id}
                                onClick={() => {
                                  setSelectedTask(task);
                                  setShowTaskDetailModal(true);
                                }}
                                className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                                  overdue && task.status !== 'COMPLETED' && task.status !== 'VERIFIED'
                                    ? 'border-red-300 bg-red-50'
                                    : task.status === 'VERIFIED'
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="text-gray-600 mt-1">{getTaskTypeIcon(task.taskType)}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <h4 className="text-sm font-semibold text-gray-900">{task.title}</h4>
                                      <div className="flex gap-2 flex-shrink-0">
                                        <Badge variant={getTaskPriorityBadgeVariant(task.priority)}>
                                          {task.priority}
                                        </Badge>
                                        <Badge variant={getTaskStatusBadgeVariant(task.status)}>
                                          {task.status}
                                        </Badge>
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                      <span className={overdue && task.status !== 'COMPLETED' && task.status !== 'VERIFIED' ? 'text-red-600 font-semibold' : ''}>
                                        {getDaysUntilDue(task.dueDate)}
                                      </span>
                                      <span>•</span>
                                      <span>Assigned by {task.assignedByName || 'Staff'}</span>
                                      {task.completedAt && (
                                        <>
                                          <span>•</span>
                                          <span className="text-green-600">✓ Completed {formatDate(task.completedAt)}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Actions & Tracking */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    onClick={() => window.location.href = `tel:${inquiry.phoneNumber}`} 
                    variant="outline" 
                    className="justify-center text-gray-900 hover:bg-gray-100"
                  >
                    📞 Call
                  </Button>
                  <Button 
                    onClick={() => window.location.href = `mailto:${inquiry.email}`} 
                    variant="outline" 
                    className="justify-center text-gray-900 hover:bg-gray-100"
                  >
                    ✉️ Email
                  </Button>
                </div>
                {!hasUserAccount && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-700 mb-2">
                      This inquiry doesn't have a user account yet. Create one to allow the client to log in, view their inquiry status, upload documents, and message your team.
                    </p>
                    <Button 
                      onClick={() => setShowProspectDialog(true)}
                      className="w-full bg-aalaw-red hover:bg-red-700 text-white"
                    >
                      👤 Create Client Account
                    </Button>
                  </div>
                )}
                {hasUserAccount && inquiry.userId && (
                  <Button 
                    onClick={() => navigate(`/messages?clientId=${inquiry.userId}`)} 
                    variant="outline" 
                    className="w-full justify-start text-gray-900 hover:bg-gray-100"
                  >
                    💬 Send Message
                  </Button>
                )}
                <Button onClick={handleLogCall} variant="outline" className="w-full justify-start text-gray-900 hover:bg-gray-100">
                  📋 Log Call
                </Button>
                <Button onClick={() => setShowNotesModal(true)} variant="outline" className="w-full justify-start text-gray-900 hover:bg-gray-100">
                  📝 Add Note
                </Button>
                <Button 
                  onClick={() => setShowVideoConferenceModal(true)} 
                  variant="outline" 
                  className="w-full justify-start text-gray-900 hover:bg-gray-100"
                >
                  📹 Schedule Video Conference
                </Button>
                {inquiry.status === 'ACCEPTED' && (
                  <Button onClick={() => setShowConvertModal(true)} className="w-full justify-start bg-green-600 text-white hover:bg-green-700">
                    👤 Assign Attorney / Case Manager
                  </Button>
                )}
                <select
                  value={currentStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                >
                  <option value="NEW">New</option>
                  <option value="REVIEWING">Reviewing</option>
                  <option value="ACCEPTED">Accepted (Converts to Case)</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </CardContent>
            </Card>

            {/* Contact Tracking */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Last Contacted</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {inquiry.lastContactedAt ? formatDateTime(inquiry.lastContactedAt) : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Last Contact Method</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {inquiry.lastContactMethod || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Follow-up Date</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {inquiry.followUpDate ? formatDate(inquiry.followUpDate) : 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Assigned To</label>
                  <p className="text-sm text-gray-900 mt-1">{inquiry.assignedToName || 'Unassigned'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Linked User Account Information */}
            {inquiry.userId && (
              <Card>
                <CardHeader>
                  <CardTitle>Linked Client Account</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-blue-900">
                          {inquiry.firstName} {inquiry.lastName}
                        </p>
                        <p className="text-xs text-blue-700">{inquiry.email}</p>
                      </div>
                      <Badge variant="info" className="text-xs">
                        CLIENT
                      </Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600">User ID</label>
                      <p className="text-xs text-gray-900 mt-1 font-mono truncate" title={inquiry.userId}>
                        {inquiry.userId.substring(0, 8)}...
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Account Status</label>
                      <p className="text-xs mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </p>
                    </div>
                  </div>
                  {inquiry.caseId && (
                    <div>
                      <label className="text-xs font-medium text-gray-600">Linked Case</label>
                      <p className="text-xs text-gray-900 mt-1 font-mono truncate" title={inquiry.caseId}>
                        {inquiry.caseId.substring(0, 8)}...
                      </p>
                    </div>
                  )}
                  <Link 
                    to={`/users/${inquiry.userId}`}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View User Details →
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Case Manager Assignment */}
            <CaseManagerAssignment
              inquiryId={id!}
              currentAssignedCaseManager={inquiry.assignedCaseManager}
              onAssignmentChange={() => refetch()}
            />

            {/* Video Conferences */}
            <VideoConferencesList
              conferences={videoConferences as any}
              loading={videoConferencesLoading}
              onRefetch={refetchVideoConferences}
              emptyMessage="No video conferences scheduled for this inquiry"
              showScheduleButton={true}
              onScheduleClick={() => setShowVideoConferenceModal(true)}
            />

            {/* Internal Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Internal Notes</CardTitle>
              </CardHeader>
              <CardContent>
                {(inquiry.activityLog?.filter((activity: any) => activity.activityType === 'NOTE_ADDED').length ?? 0) > 0 ? (
                  <div className="space-y-3">
                    {(inquiry.activityLog ?? [])
                      .filter((activity: any) => activity.activityType === 'NOTE_ADDED')
                      .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                      .map((activity: any) => (
                        <div key={activity.id} className="pb-3 border-b border-gray-100 last:border-0">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{activity.details}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-gray-500">{activity.performedByName}</span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-400">{formatDateTime(activity.timestamp)}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No notes yet. Click "Add Note" to add one.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Convert to Case Modal */}
      {showConvertModal && (
        <ConvertToCaseModal
          inquiryId={id!}
          inquiry={inquiry}
          onClose={() => setShowConvertModal(false)}
          onSuccess={(caseData) => {
            setShowConvertModal(false);
            navigate(`/cases/${caseData.id}`);
          }}
        />
      )}

      {/* Add Note Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Add Note</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter your note..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <div className="flex gap-2">
                <Button onClick={handleAddNote} className="flex-1 bg-red-600 text-white hover:bg-red-700">
                  Save Note
                </Button>
                <Button onClick={() => setShowNotesModal(false)} variant="outline" className="flex-1 text-gray-900 hover:bg-gray-100">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Viewer Modal */}
      <EmailViewer
        isOpen={showEmailViewer}
        onClose={() => setShowEmailViewer(false)}
        emailUrl={emailUrl}
        inquiryName={`${inquiry?.firstName} ${inquiry?.lastName}`}
      />

      {/* User Account Confirmation Dialog */}
      {inquiry && (
        <ProspectConfirmationDialog
          open={showProspectDialog}
          inquiry={{
            id: inquiry.id,
            email: inquiry.email,
            firstName: inquiry.firstName,
            lastName: inquiry.lastName,
            phoneNumber: inquiry.phoneNumber,
          }}
          onConfirm={handleCreateUserConfirm}
          onCancel={handleProspectCancel}
          isLoading={prospectLoading}
        />
      )}

      {/* Rejection Confirmation Dialog */}
      <Dialog open={showRejectionDialog} onOpenChange={(isOpen) => !isOpen && handleRejectionCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Inquiry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-900">
                This inquiry has a linked client account. Would you like to disable the client's account?
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">
                {inquiry?.firstName} {inquiry?.lastName}
              </p>
              <p className="text-xs text-gray-600">{inquiry?.email}</p>
            </div>
            <p className="text-sm text-gray-600">
              Disabling the account will prevent the client from sending messages or uploading documents, 
              but they can still view their inquiry details.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={() => handleRejection(true)}
                disabled={rejectionLoading}
                variant="destructive"
                className="w-full"
              >
                {rejectionLoading ? 'Processing...' : 'Reject & Disable Account'}
              </Button>
              <Button
                onClick={() => handleRejection(false)}
                disabled={rejectionLoading}
                variant="outline"
                className="w-full"
              >
                {rejectionLoading ? 'Processing...' : 'Reject Only (Keep Account Active)'}
              </Button>
              <Button
                onClick={handleRejectionCancel}
                disabled={rejectionLoading}
                variant="ghost"
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Video Conference Modal */}
      <ScheduleConferenceModal
        isOpen={showVideoConferenceModal}
        onClose={() => setShowVideoConferenceModal(false)}
        onCreated={() => {
          refetchVideoConferences();
          setShowVideoConferenceModal(false);
        }}
        preSelectedInquiry={{
          id: inquiry?.id || '',
          firstName: inquiry?.firstName || '',
          lastName: inquiry?.lastName || '',
          email: inquiry?.email,
          phoneNumber: inquiry?.phoneNumber,
        }}
      />

      {/* File Viewer Modal */}
      <FileViewerModal
        visible={fileViewerVisible}
        files={fileViewerFiles}
        initialIndex={fileViewerIndex}
        onClose={() => setFileViewerVisible(false)}
      />

      {/* Create Task Modal */}
      <Dialog open={showCreateTaskModal} onOpenChange={setShowCreateTaskModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
                placeholder="Enter task title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
                rows={3}
                placeholder="Describe what the client needs to do"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                <select
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
                >
                  <option value="DOCUMENT_UPLOAD">Document Upload</option>
                  <option value="FORM_COMPLETION">Form Completion</option>
                  <option value="APPOINTMENT">Appointment</option>
                  <option value="COMMUNICATION">Communication</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={taskPriority}
                  onChange={(e) => setTaskPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
              <input
                type="date"
                value={taskDueDate}
                onChange={(e) => setTaskDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff Notes (optional)</label>
              <textarea
                value={taskStaffNotes}
                onChange={(e) => setTaskStaffNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
                rows={2}
                placeholder="Internal notes for staff"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreateTaskModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTask}>
                Assign Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          isOpen={showTaskDetailModal}
          onClose={() => {
            setShowTaskDetailModal(false);
            setSelectedTask(null);
          }}
          onTaskUpdated={() => {
            refetchTasks();
          }}
        />
      )}
    </div>
  );
}

// Convert to Case Modal Component
function ConvertToCaseModal({
  inquiryId,
  inquiry,
  onClose,
  onSuccess,
}: {
  inquiryId: string;
  inquiry: any;
  onClose: () => void;
  onSuccess: (caseData: any) => void;
}) {
  const [assignedTo, setAssignedTo] = useState(inquiry.assignedTo || '');
  
  // Query to get list of staff members
  const { data: staffData, loading: staffLoading } = useQuery<{ listStaff: Array<{ id: string; firstName: string; lastName: string; role: string; email: string }> }>(gql`
    query ListStaff {
      listStaff {
        id
        firstName
        lastName
        role
        email
      }
    }
  `);
  
  const [convertToCase, { loading }] = useMutation(CONVERT_INQUIRY_TO_CASE, {
    refetchQueries: ['ListInquiries', 'GetInquiry', 'ListMyCases'],
    awaitRefetchQueries: true,
  });

  const handleConvert = async () => {
    if (!assignedTo) {
      alert('Please assign this inquiry to a staff member before converting to a case');
      return;
    }

    try {
      const result = await convertToCase({
        variables: { 
          id: inquiryId,
          assignedAttorneyId: assignedTo,
        },
      });
      onSuccess((result.data as any).convertInquiryToCase);
    } catch (error: any) {
      alert('Error converting to case: ' + error.message);
    }
  };

  const staffMembers = staffData?.listStaff || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Convert Inquiry to Case</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              This will create a new case and client account for:
            </p>
            <p className="text-sm font-semibold text-blue-900 mt-2">
              {inquiry.firstName} {inquiry.lastName} ({inquiry.email})
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Case Type</label>
              <p className="text-sm text-gray-900 mt-1">{inquiry.incidentType}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <p className="text-sm text-gray-900 mt-1">{inquiry.description}</p>
            </div>
            
            {/* Assignment Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Assign To <span className="text-red-600">*</span>
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Select the attorney who will handle this case
              </p>
              {staffLoading ? (
                <div className="text-sm text-gray-500">Loading staff members...</div>
              ) : (
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                >
                  <option value="">-- Select Staff Member --</option>
                  {staffMembers.map((staff: any) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.firstName} {staff.lastName} ({staff.role}) - {staff.email}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleConvert} disabled={loading || staffLoading} className="flex-1 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400">
              {loading ? 'Converting...' : 'Convert to Case'}
            </Button>
            <Button onClick={onClose} variant="outline" className="flex-1 text-gray-900 hover:bg-gray-100">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
